import { Fragment, useEffect, useState } from 'react';
import { api } from '../api/client';
import { Badge, Bandeau, Carte, Kpi, Squelette } from '../components/ui';
import { montant, montantCourt, roleLabel } from '../lib/format';
import type { BilanVehicules } from '../api/types';
import { T } from '../i18n';

interface Flotte { tauxUtilisation: number; revPACMinor: number; prixMoyenJourMinor: number; dureeMoyenneJours: number; chiffreAffairesMinor: number; nbLocations: number; diagnostic: string }
interface Rentab { vehicules: { vehicleId: string; libelle: string; immatriculation: string; joursLoues: number; tauxUtilisation: number; chiffreAffairesMinor: number; coutEntretienMinor: number; margeNetteMinor: number; verdict: string }[] }
interface Refus { nombre: number; valeurPerdueMinor: number; parCategorie: { categorie: string; nombre: number; valeurMinor: number }[] }
interface Equipe { employes: { userId: string; nom: string; role: string; contratsCrees: number; reservationsCreees: number; caEncaisseMinor: number; etatsDesLieuxSaisis: number; dommagesNonConstates: number; coutDommagesNonConstatesMinor: number; demandesCorrection: number }[] }

const VERDICT = (): Record<string, { s: string; l: string }> => ({
  a_garder: { s: 'en_cours', l: T.analytique.verdicts.a_garder! },
  a_surveiller: { s: 'retour', l: T.analytique.verdicts.a_surveiller! },
  a_vendre: { s: 'retard', l: T.analytique.verdicts.a_vendre! },
});

/** « août 26 » plutot que « 2026-08 » : on lit un tableau, pas une base. */
const libelleCourt = (mois: string) => {
  const [a, m] = mois.split('-').map(Number);
  return new Date(a!, (m ?? 1) - 1, 1).toLocaleDateString(T.analytique.localeMois, { month: 'short', year: '2-digit' });
};

/** Une case se lit d'un coup d'oeil : plus c'est plein, plus c'est vert. */
function fondTaux(taux: number): string | undefined {
  if (taux <= 0) return undefined;
  if (taux >= 75) return 'var(--succes-doux)';
  if (taux >= 45) return 'var(--accent-doux)';
  return 'var(--danger-doux)';
}

export default function Analytique() {
  const [f, setF] = useState<Flotte | null>(null);
  const [r, setR] = useState<Rentab | null>(null);
  const [ref, setRef] = useState<Refus | null>(null);
  const [eq, setEq] = useState<Equipe | null>(null);
  const [bilan, setBilan] = useState<BilanVehicules | null>(null);
  const [nbMois, setNbMois] = useState(6);
  const [detail, setDetail] = useState<string | null>(null);

  useEffect(() => {
    api.get<Flotte>('/analytique/flotte').then(setF);
    api.get<Rentab>('/analytique/rentabilite').then(setR);
    api.get<Refus>('/analytique/demandes-refusees').then(setRef);
    api.get<BilanVehicules>(`/analytique/bilan-vehicules?nb=${nbMois}`).then(setBilan).catch(() => {});
    api.get<Equipe>('/analytique/equipe').then(setEq);
  }, [nbMois]);

  if (!f) return <Squelette lignes={8} />;
  const tx = T.analytique;
  const verdict = VERDICT();

  return (
    <>
      <div className="entete">
        <div className="titres"><h1>{tx.titre}</h1><p>{tx.sousTitre}</p></div>
        <div className="actions">
          <select className="btn btn-s" value={nbMois} onChange={(e) => { setNbMois(Number(e.target.value)); setBilan(null); }}
                  style={{ paddingRight: 8 }}>
            <option value={3}>{tx.nbMois(3)}</option><option value={6}>{tx.nbMois(6)}</option>
            <option value={12}>{tx.nbMois(12)}</option>
          </select>
        </div>
      </div>

      <div className="grille g4">
        <Kpi libelle={tx.kpi.utilisation} valeur={`${f.tauxUtilisation} %`} detail={tx.kpi.utilisationDetail} />
        <Kpi libelle={tx.kpi.revenu} valeur={montantCourt(f.revPACMinor)} detail={tx.kpi.revenuDetail} />
        <Kpi libelle={tx.kpi.prixMoyen} valeur={montantCourt(f.prixMoyenJourMinor)} detail={tx.kpi.prixMoyenDetail} />
        <Kpi libelle={tx.kpi.duree} valeur={tx.kpi.dureeValeur(f.dureeMoyenneJours)} detail={tx.kpi.dureeDetail(f.nbLocations)} />
      </div>

      <div style={{ marginTop: 14 }}><Bandeau type="info" titre={tx.diagnostic}>{f.diagnostic}</Bandeau></div>

      {ref && ref.nombre > 0 && (
        <div style={{ marginTop: 16 }}>
          <Carte titre={tx.refus.titre} className="signature-kpi">
            <div className="kpi">
              <div className="v">{montant(ref.valeurPerdueMinor)}</div>
              <div className="d">{tx.refus.detail(ref.nombre)}</div>
            </div>
            <table className="tbl compact" style={{ marginTop: 12 }}>
              <thead><tr><th>{tx.refus.categorie}</th><th className="droite">{tx.refus.refus}</th><th className="droite">{tx.refus.manque}</th></tr></thead>
              <tbody>
                {ref.parCategorie.map((c) => (
                  <tr key={c.categorie}><td>{c.categorie}</td><td className="droite num">{c.nombre}</td><td className="droite montant">{montant(c.valeurMinor)}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="mini sourdine" style={{ marginTop: 10 }}>
              {tx.refus.note}
            </p>
          </Carte>
        </div>
      )}

      {/*
        * Le bilan par voiture ET par mois.
        *
        * Le taux global d'une flotte melange la Clio qui tourne toute l'annee et
        * le monospace qui ne sort qu'en ete : il ne dit rien d'utile. Ici on voit
        * qu'une voiture ne travaille que trois mois sur douze — et on decide.
        */}
      {bilan && (
        <div style={{ marginTop: 16 }}>
          <Carte pad={false} titre={tx.parMois.titre}>
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr>
                    <th>{tx.parMois.vehicule}</th>
                    {bilan.mois.map((m) => <th key={m} className="droite">{libelleCourt(m)}</th>)}
                    <th className="droite">{tx.parMois.total}</th>
                    <th className="droite">{tx.parMois.marge}</th>
                  </tr>
                </thead>
                <tbody>
                  {bilan.vehicules.map((v) => (
                    <Fragment key={v.vehicleId}>
                      <tr onClick={() => setDetail(detail === v.vehicleId ? null : v.vehicleId)} style={{ cursor: 'pointer' }}>
                        <td>
                          <b>{v.libelle}</b>
                          <div className="mini sourdine">{v.immatriculation} · {v.categorie}</div>
                        </td>
                        {v.mois.map((m) => (
                          <td key={m.mois} className="droite num"
                              title={tx.parMois.infobulle(m.joursLoues, montant(m.caTotalMinor))}
                              style={{ background: fondTaux(m.tauxUtilisation) }}>
                            {m.joursDisponibles === 0 ? '—' : `${Math.round(m.tauxUtilisation)} %`}
                          </td>
                        ))}
                        <td className="droite montant">{montantCourt(v.total.caTotalMinor)}</td>
                        <td className="droite montant" style={{ color: v.total.margeMinor < 0 ? 'var(--danger)' : undefined }}>
                          {montantCourt(v.total.margeMinor)}
                        </td>
                      </tr>
                      {detail === v.vehicleId && (
                        <tr>
                          <td colSpan={bilan.mois.length + 3} style={{ background: 'var(--zebre)', padding: 12 }}>
                            <table className="tbl compact">
                              <thead>
                                <tr>
                                  <th>{tx.parMois.colonnes.mois}</th><th className="droite">{tx.parMois.colonnes.locations}</th><th className="droite">{tx.parMois.colonnes.joursLoues}</th>
                                  <th className="droite">{tx.parMois.colonnes.caLocation}</th><th className="droite">{tx.parMois.colonnes.supplements}</th>
                                  <th className="droite">{tx.parMois.colonnes.prixMoyen}</th>
                                  <th className="droite">{tx.parMois.colonnes.entretien}</th><th className="droite">{tx.parMois.colonnes.joursGarage}</th>
                                  <th className="droite">{tx.parMois.colonnes.marge}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {v.mois.map((m) => (
                                  <tr key={m.mois}>
                                    <td>{libelleCourt(m.mois)}</td>
                                    <td className="droite num">{m.nbLocations || '—'}</td>
                                    <td className="droite num">{m.joursLoues || '—'} / {m.joursDisponibles}</td>
                                    <td className="droite montant">{m.caLocationMinor ? montant(m.caLocationMinor) : '—'}</td>
                                    <td className="droite montant">{m.caSupplementsMinor ? montant(m.caSupplementsMinor) : '—'}</td>
                                    <td className="droite montant">{m.prixMoyenJourMinor ? montant(m.prixMoyenJourMinor) : '—'}</td>
                                    <td className="droite montant" style={m.coutEntretienMinor ? { color: 'var(--danger)' } : undefined}>
                                      {m.coutEntretienMinor ? montant(m.coutEntretienMinor) : '—'}
                                    </td>
                                    <td className="droite num">{m.joursImmobilise || '—'}</td>
                                    <td className="droite montant">{montant(m.margeMinor)}</td>
                                  </tr>
                                ))}
                                <tr style={{ borderTop: '2px solid var(--bord-fort)' }}>
                                  <td><b>{tx.parMois.total}</b></td>
                                  <td className="droite num"><b>{v.total.nbLocations}</b></td>
                                  <td className="droite num"><b>{v.total.joursLoues} / {v.total.joursDisponibles}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.caLocationMinor)}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.caSupplementsMinor)}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.prixMoyenJourMinor)}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.coutEntretienMinor)}</b></td>
                                  <td className="droite num"><b>{v.total.joursImmobilise}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.margeMinor)}</b></td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Carte>
          <p className="mini sourdine" style={{ marginTop: 12 }}>
            {tx.parMois.noteAvant}<b>{tx.parMois.noteGras}</b>{tx.parMois.noteApres}
          </p>
        </div>
      )}

      {r && (
        <div style={{ marginTop: 16 }}>
          <Carte titre={tx.rentabilite.titre} pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr><th>{tx.rentabilite.colonnes.vehicule}</th><th>{tx.rentabilite.colonnes.immatriculation}</th><th className="droite">{tx.rentabilite.colonnes.joursLoues}</th><th className="droite">{tx.rentabilite.colonnes.utilisation}</th>
                      <th className="droite">{tx.rentabilite.colonnes.ca}</th><th className="droite">{tx.rentabilite.colonnes.entretien}</th><th className="droite">{tx.rentabilite.colonnes.marge}</th><th>{tx.rentabilite.colonnes.verdict}</th></tr>
                </thead>
                <tbody>
                  {r.vehicules.map((v) => (
                    <tr key={v.vehicleId}>
                      <td><b>{v.libelle}</b></td>
                      <td className="num mini sourdine">{v.immatriculation}</td>
                      <td className="droite num">{v.joursLoues}</td>
                      <td className="droite num">{v.tauxUtilisation} %</td>
                      <td className="droite montant">{montantCourt(v.chiffreAffairesMinor)}</td>
                      <td className="droite montant sourdine">{montantCourt(v.coutEntretienMinor)}</td>
                      <td className="droite montant" style={{ color: v.margeNetteMinor < 0 ? 'var(--danger)' : undefined }}>{montantCourt(v.margeNetteMinor)}</td>
                      <td><Badge statut={verdict[v.verdict]?.s ?? 'terminee'} libelle={verdict[v.verdict]?.l ?? v.verdict} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Carte>
        </div>
      )}

      {eq && (
        <div style={{ marginTop: 16 }}>
          <Carte titre={tx.equipe.titre} pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr><th>{tx.equipe.colonnes.personne}</th><th className="droite">{tx.equipe.colonnes.reservations}</th><th className="droite">{tx.equipe.colonnes.contrats}</th>
                      <th className="droite">{tx.equipe.colonnes.encaisse}</th><th className="droite">{tx.equipe.colonnes.edl}</th>
                      <th className="droite">{tx.equipe.colonnes.dommages}</th><th className="droite">{tx.equipe.colonnes.corrections}</th></tr>
                </thead>
                <tbody>
                  {eq.employes.map((e) => (
                    <tr key={e.userId}>
                      <td><b>{e.nom}</b> <span className="mini sourdine">{roleLabel(e.role)}</span></td>
                      <td className="droite num">{e.reservationsCreees}</td>
                      <td className="droite num">{e.contratsCrees}</td>
                      <td className="droite montant">{montantCourt(e.caEncaisseMinor)}</td>
                      <td className="droite num">{e.etatsDesLieuxSaisis}</td>
                      <td className="droite num" style={{ background: e.dommagesNonConstates > 0 ? 'var(--danger-doux)' : undefined, color: e.dommagesNonConstates > 0 ? 'var(--danger)' : undefined }}
                          title={tx.equipe.dommagesInfobulle}>
                        {e.dommagesNonConstates}{e.coutDommagesNonConstatesMinor > 0 && ` · ${montantCourt(e.coutDommagesNonConstatesMinor)}`}
                      </td>
                      <td className="droite num">{e.demandesCorrection}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="carte-p">
              <p className="mini sourdine">
                {tx.equipe.note}
              </p>
            </div>
          </Carte>
        </div>
      )}
    </>
  );
}
